import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Button,
  Box,
  Select,
  MenuItem
} from "@mui/material";
import PaginatedSearchSelect from '../../../../components/PaginatedSearchSelect';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  StyledDialog,
  ModalTitle,
  ModalContent,
  FormControl,
  FormLabel,
  Row,
  ActionButtons,
  CancelButton,
  SubmitButton
} from './styles';

import AlertPopup from '../../../../components/common/AlertPopup';

import { fetchFacilityList, fetchFacilityUsers } from '../../../../modules/facility/actions';
import { selectFacilityUsers } from '../../../../modules/facility/selectors';
import { fetchRoleList } from '../../../../modules/role/actions';
import { IRole } from '../../../../modules/role/types';
import { createFacilityShift, updateFacilityShift, createPastFacilityShift, updatePastFacilityShift } from '../../../../modules/facilityShifts/actions';
import { fetchPastShiftWorkerList } from '../../../../modules/worker/actions';
import { AppDispatch } from '../../../../store';

interface CreateShiftModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
  initialDate?: Dayjs;
  initialTime?: Dayjs;
  initialFacility?: Option | null;
  editData?: any;
}

interface ShiftSlot {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  isSameDay: boolean;
  selectedWorkers: (Option | null)[];
}

interface Option {
  id: number;
  label: string;
}

const CreateShiftModal: React.FC<CreateShiftModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialDate,
  initialTime,
  initialFacility,
  editData
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [shiftSlots, setShiftSlots] = useState<ShiftSlot[]>([{ 
    startDate: null, 
    endDate: null, 
    startTime: null, 
    endTime: null,
    isSameDay: true,
    selectedWorkers: []
  }]);

  // State for selections (IDs)
  const [selectedFacility, setSelectedFacility] = useState<Option | null>(null);
  const [position, setPosition] = useState<Option | null>(null);
  const [workersRequired, setWorkersRequired] = useState("");
  const [experience, setExperience] = useState("");
  const [appointMember, setAppointMember] = useState("");
  const [notes, setNotes] = useState(editData?.additional_notes || '');
  const [createMultiple, setCreateMultiple] = useState(false);
  const [isSleepover, setIsSleepover] = useState(false);
  const [label, setLabel] = useState(editData?.label || "");

  const isLocked = false;

  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  // Redux Selectors
  const facilityUsers = useSelector(selectFacilityUsers);

  const isPastSlot = (slot: ShiftSlot) => {
    if (!slot.startDate || !slot.startTime) return false;
    const start = slot.startDate.hour(slot.startTime.hour()).minute(slot.startTime.minute());
    return start.isBefore(dayjs());
  };




  // Fetch Initial Data (Facilities & Roles)
  useEffect(() => {
    if (open) {
      // Fetch Facilities
      dispatch(fetchFacilityList({ page: 1, pageSize: 100, search: '' }));

      // Fetch Roles (Simplified: fetching first 100)
      dispatch(fetchRoleList({ page: 1, pageSize: 100, search: '' })); // Trigger fetch but we use fetchOptions prop now

      // Reset fields
      setSelectedFacility(initialFacility && !editData ? initialFacility : null);
      setPosition(null);
      setWorkersRequired("");
      setExperience("");
      setAppointMember("");
      setNotes("");
      setCreateMultiple(false);
      setIsSleepover(false);
      setLabel("");
      setShiftSlots([{ 
        startDate: null, 
        endDate: null, 
        startTime: null, 
        endTime: null,
        isSameDay: true,
        selectedWorkers: []
      }]);

      if (editData) {
        console.log("Populating Edit Data:", editData);
        // Populate for Edit Mode
        const start = dayjs(editData.start_date_time);
        const end = dayjs(editData.end_date_time);

        let assignedWorkers: any[] = [];
        if (editData.shift_assignment && Array.isArray(editData.shift_assignment)) {
          assignedWorkers = editData.shift_assignment
            .filter((a: any) => !['worker_cancelled', 'facility_reject', 'cancelled', 'rejected'].includes(a.status?.toLowerCase()))
            .map((a: any) => a.worker)
            .filter((w: any) => w !== null && w !== undefined);
        } else if (editData.assigned_workers && Array.isArray(editData.assigned_workers)) {
          assignedWorkers = editData.assigned_workers;
        }

        const workersRequiredCount = editData.workers_required || 0;
        const initialSelectedWorkers = Array(workersRequiredCount).fill(null);
        assignedWorkers.forEach((w: any, i: number) => {
          if (i < initialSelectedWorkers.length) {
            initialSelectedWorkers[i] = { 
              id: w.worker_id || w.id, 
              label: `${w.user?.first_name || ''} ${w.user?.last_name || ''}`.trim() || w.label || w.name || "Unknown Worker" 
            };
          }
        });

        setShiftSlots([{ 
          startDate: start, 
          endDate: end, 
          startTime: start, 
          endTime: end,
          isSameDay: start.isSame(end, 'day'),
          selectedWorkers: initialSelectedWorkers
        }]);

        // Safely extract IDs
        let facOption: Option | null = null;
        if (editData.facility) {
          if (typeof editData.facility === 'object') {
            const fId = editData.facility.id || editData.facility.facility_id;
            const fName = editData.facility.facility_name || editData.facility.name || "Unknown Facility";
            if (fId) facOption = { id: fId, label: fName };
          } else {
            facOption = { id: editData.facility, label: `Facility ${editData.facility}` };
          }
        }
        setSelectedFacility(facOption);

        // Position: Try ID first, then fallback
        const posId = editData.position_role_id
          || ((editData.position_role && typeof editData.position_role === 'object') ? (editData.position_role.id || editData.position_role.role_id) : editData.position_role);

        let posLabel = editData.role_name || (editData.position_role && typeof editData.position_role === 'object' && editData.position_role.role_name) || "Unknown Role";

        // If editData comes from allShifts map, 'role' property holds the name
        if (editData.role && typeof editData.role === 'string') {
          posLabel = editData.role;
        } else if (typeof editData.position_role === 'string') {
          // If position_role is just a string (name), use it
          posLabel = editData.position_role;
        }

        if (posId) {
          setPosition({ id: Number(posId), label: String(posLabel) });
        } else {
          setPosition(null);
        }

        setWorkersRequired(editData.workers_required?.toString() || "");
        setExperience(editData.experience_required?.toString() || "");
        setIsSleepover(!!editData.is_sleepover);
        setLabel(editData.label || "");
        setNotes(editData.additional_notes || "");

        // Appoint Member
        // Try to find an ID in attendance_manager
        const memberId = (editData.attendance_manager && typeof editData.attendance_manager === 'object')
          ? (editData.attendance_manager.id || editData.attendance_manager.user_id || editData.attendance_manager.pk)
          : editData.attendance_manager;
        setAppointMember(memberId?.toString() || "");

      } else {
        // Create Mode
        if (initialDate || initialTime) {
          setShiftSlots([{
            startDate: initialDate || null,
            endDate: initialDate || null,
            startTime: initialTime || null,
            endTime: initialTime ? initialTime.add(8, 'hour') : null,
            isSameDay: true,
            selectedWorkers: []
          }]);
        }
      }
    }
  }, [open, initialDate, initialTime, initialFacility, dispatch, editData]);; // Added editData to deps


  // Fetch Facility Users (for Members) when Facility changes
  useEffect(() => {
    if (selectedFacility) {
      console.log('Fetching users for facility:', selectedFacility.label);
      dispatch(fetchFacilityUsers(Number(selectedFacility.id)));

      // Only reset member if this facility is different from the one in editData (if editing)
      // preventing race condition where setting facility clears the populated member
      let isSameAsEdit = false;
      if (editData) {
        const editFacId = (editData.facility && typeof editData.facility === 'object')
          ? (editData.facility.id || editData.facility.facility_id)
          : editData.facility;
        isSameAsEdit = String(editFacId) === String(selectedFacility.id);
      }

      if (!isSameAsEdit) {
        setAppointMember("");
      }
    }
  }, [selectedFacility, dispatch, editData]);

  useEffect(() => {
    console.log('Facility Users Updated:', facilityUsers);

    // Fallback: If appointMember is empty but we have editData details, try to match by Email
    if (editData && !appointMember && facilityUsers.length > 0 && editData.attendance_manager) {
      const managerEmail = editData.attendance_manager.email;
      if (managerEmail) {
        const found = facilityUsers.find(u => u.user.email === managerEmail);
        if (found) {
          console.log("Found attendance manager by email:", found);
          setAppointMember(found.user.user_id.toString());
        }
      }
    }
  }, [facilityUsers, editData, appointMember]);
  

  const handleRemoveSlot = (index: number) => {
    setShiftSlots(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddSlot = () => {
    const lastSlot = shiftSlots[shiftSlots.length - 1];
    const count = Number(workersRequired) || 0;
    
    setShiftSlots(prev => [...prev, { 
      startDate: lastSlot?.startDate || null,
      endDate: lastSlot?.endDate || null,
      startTime: null, 
      endTime: null,
      isSameDay: lastSlot?.isSameDay ?? true,
      selectedWorkers: Array(count).fill(null)
    }]);
  };

  const handleSlotChange = (index: number, field: keyof ShiftSlot, value: any) => {
    setShiftSlots(prev => {
      const newSlots = [...prev];
      const currentSlot = { ...newSlots[index] };
      
      if (field === 'isSameDay') {
        currentSlot.isSameDay = value;
        if (value && currentSlot.startDate) {
          currentSlot.endDate = currentSlot.startDate;
        }
      } else if (field === 'startDate') {
        currentSlot.startDate = value;
        if (currentSlot.isSameDay) {
          currentSlot.endDate = value;
        }
      } else if (field === 'endDate') {
        currentSlot.endDate = value;
      } else if (field === 'startTime') {
        currentSlot.startTime = value;
      } else if (field === 'endTime') {
        currentSlot.endTime = value;
      } else if (field === 'selectedWorkers') {
        currentSlot.selectedWorkers = value;
      }

      // Time validation check
      if ((field === 'endTime' || field === 'endDate' || field === 'startDate' || field === 'startTime') && value && currentSlot.startTime && currentSlot.startDate) {
        if (currentSlot.endTime && currentSlot.endDate) {
          const start = currentSlot.startDate.hour(currentSlot.startTime.hour()).minute(currentSlot.startTime.minute());
          const end = currentSlot.endDate.hour(currentSlot.endTime.hour()).minute(currentSlot.endTime.minute());

          if (start && end) {
            if (end.isBefore(start)) {
              setAlert({ message: "End time cannot be before start time", severity: "error" });
            }
          }
        }
      }

      newSlots[index] = currentSlot;

      const count = Number(workersRequired) || 0;
      if (currentSlot.selectedWorkers.length !== count) {
        if (currentSlot.selectedWorkers.length < count) {
          currentSlot.selectedWorkers = [...currentSlot.selectedWorkers, ...Array(count - currentSlot.selectedWorkers.length).fill(null)];
        } else {
          currentSlot.selectedWorkers = currentSlot.selectedWorkers.slice(0, count);
        }
      }

      return newSlots;
    });
  };

  const handleSubmit = () => {
    // Validate inputs
    const isInvalid = shiftSlots.some(slot => !slot.startDate || !slot.endDate || !slot.startTime || !slot.endTime) || !selectedFacility || !position || !workersRequired;
    if (isInvalid) {
      setAlert({ message: "Please fill in required fields", severity: "error" });
      return;
    }

    // Check for past dates and worker assignment per slot
    let workerMissing = false;
    shiftSlots.forEach(slot => {
      if (isPastSlot(slot)) {
        const filledCount = slot.selectedWorkers.filter(w => w !== null).length;
        if (filledCount < Number(workersRequired)) {
          workerMissing = true;
        }
      }
    });

    if (workerMissing) {
      setAlert({ message: "Please select workers for all past shifts.", severity: "error" });
      return;
    }

    // Check for End Time < Start Time
    const invalidTime = shiftSlots.some(slot => {
      if (!slot.startDate || !slot.endDate || !slot.startTime || !slot.endTime) return false;
      const start = slot.startDate.hour(slot.startTime.hour()).minute(slot.startTime.minute());
      const end = slot.endDate.hour(slot.endTime.hour()).minute(slot.endTime.minute());

      if (end.isBefore(start)) return true; // Checks for negative duration
      return false;
    });

    if (invalidTime) {
      setAlert({ message: "Shift end time must be after start time", severity: "error" });
      return;
    }

    setLoading(true);

    // Payload construction
    const constructPayload = (slot: ShiftSlot) => ({
      start_date_time: (slot.startDate as Dayjs).hour(slot.startTime?.hour() || 0).minute(slot.startTime?.minute() || 0).format(),
      end_date_time: (slot.endDate as Dayjs).hour(slot.endTime?.hour() || 0).minute(slot.endTime?.minute() || 0).format(),
      facility_id: Number(selectedFacility?.id),
      position_role: Number(position?.id),
      workers_required: Number(workersRequired),
      experience_required: Number(experience),
      attendance_manager: Number(appointMember),
      is_sleepover: isSleepover,
      label: label,
      additional_notes: notes,
      assigned_workers: slot.selectedWorkers.filter(w => w !== null).map(w => Number(w?.id))
    });

    if (editData) {
      const payload = constructPayload(shiftSlots[0]);
      const isPast = isPastSlot(shiftSlots[0]);
      const updateAction = isPast ? updatePastFacilityShift : updateFacilityShift;
      dispatch(updateAction({ id: editData.id || editData.shift_id, data: payload }))
        .unwrap()
        .then((res: any) => {
          setLoading(false);
          onSuccess(res?.message || res?.data?.message);
          onClose();
        })
        .catch((error) => {
          setLoading(false);
          setAlert({ message: typeof error === 'string' ? error : 'Failed to update shift.', severity: 'error' });
        });
    } else {
      const promises = shiftSlots.map(slot => {
        const payload = constructPayload(slot);
        const isPast = isPastSlot(slot);
        const shiftAction = isPast ? createPastFacilityShift : createFacilityShift;
        return dispatch(shiftAction(payload)).unwrap();
      });

      Promise.all(promises)
        .then((results: any[]) => {
          setLoading(false);
          onSuccess(results?.[0]?.message || results?.[0]?.data?.message);
          onClose();
        })
        .catch((error) => {
          setLoading(false);
          setAlert({ message: typeof error === 'string' ? error : 'Failed to create shifts.', severity: 'error' });
        });
    }
  };
  
  const fetchFacilityOptions = React.useCallback(async (query: string, page: number) => {
    const response: any = await dispatch(fetchFacilityList({
      page,
      pageSize: 20,
      search: query,
    })).unwrap();
    return {
      options: response.results.map((f: any) => ({
        id: f.facility_id,
        label: f.facility_name
      })),
      hasMore: response.next !== null
    };
  }, [dispatch]);

  const fetchWorkerOptions = React.useCallback(async (query: string, page: number, slot: ShiftSlot) => {
    const startTimeStr = slot.startDate?.hour(slot.startTime?.hour() || 0).minute(slot.startTime?.minute() || 0).format();
    const endTimeStr = slot.endDate?.hour(slot.endTime?.hour() || 0).minute(slot.endTime?.minute() || 0).format();

    const excludeIds = slot.selectedWorkers
      .filter(w => w !== null)
      .map(w => w?.id)
      .join(',');

    const response: any = await dispatch(fetchPastShiftWorkerList({
      page,
      pageSize: 20,
      role: position?.id,
      startTime: startTimeStr,
      endTime: endTimeStr,
      excludeIds: excludeIds,
      searchTerm: query
    })).unwrap();

    return {
      options: response.results.map((w: any) => ({
        id: w.worker_id,
        label: `${w.user.first_name} ${w.user.last_name}`
      })),
      hasMore: response.next !== null
    };
  }, [dispatch, position]);


  const fetchPositionOptions = React.useCallback(async (query: string, page: number) => {
    const action: any = await dispatch(fetchRoleList({ page, pageSize: 20, search: query }));
    let items: IRole[] = [];
    if (action.payload) {
      items = Array.isArray(action.payload) ? action.payload : action.payload.results || [];
    }
    return {
      options: items.map(r => ({ id: r.role_id, label: r.role_name })),
      hasMore: false
    };
  }, [dispatch]);


  const fetchAppointMemberOptions = React.useCallback(async (query: string, page: number) => {
    if (!selectedFacility) return { options: [], hasMore: false };
    const filtered = (facilityUsers || []).filter(u =>
      `${u.user.first_name} ${u.user.last_name}`.toLowerCase().includes(query.toLowerCase())
    );
    return {
      options: filtered.map(u => ({ id: u.user.user_id, label: `${u.user.first_name} ${u.user.last_name}` })),
      hasMore: false
    };
  }, [selectedFacility, facilityUsers]);


  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ModalTitle>{editData ? 'Edit Shift' : 'Post a New Requirement'}</ModalTitle>
      <ModalContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* Sticky Header Section */}
          <Box sx={{ 
            pt: 1, 
            pb: 2, 
            mb: 1, 
            borderBottom: '1px solid #f0f0f0' 
          }}>
            {/* Facility Dropdown */}
            <FormControl>
              <FormLabel>Facility Name</FormLabel>
              <PaginatedSearchSelect<Option>
                label=""
                placeholder="Search & Select Facility"
                fetchOptions={fetchFacilityOptions}
                getOptionLabel="label"
                value={selectedFacility}
                onChange={(newValue) => setSelectedFacility(newValue)}
                disabled={isLocked}
              />
            </FormControl>

            {/* Position & Workers Row */}
            <Row sx={{ mt: 2 }}>
              <FormControl>
                <FormLabel>Position</FormLabel>
                <PaginatedSearchSelect
                  fetchOptions={fetchPositionOptions}
                  getOptionLabel="label"
                  value={position}
                  onChange={(v: any) => {
                    setPosition(v);
                    setShiftSlots(prev => prev.map(s => ({ ...s, selectedWorkers: s.selectedWorkers.map(() => null) })));
                  }}
                  placeholder="-Select a position-"
                  size='small'
                  fullWidth
                  disabled={isLocked}
                />
              </FormControl>
              <FormControl>
                <FormLabel>No of Workers Required</FormLabel>
                <Select
                  value={workersRequired}
                  onChange={(e) => {
                    const val = e.target.value;
                    const newCount = val ? Number(val) : 0;
                    setWorkersRequired(val ? String(val) : '');
                    setShiftSlots(prev => prev.map(slot => {
                      const currentCount = slot.selectedWorkers.length;
                      if (currentCount !== newCount) {
                        if (currentCount < newCount) {
                          return { ...slot, selectedWorkers: [...slot.selectedWorkers, ...Array(newCount - currentCount).fill(null)] };
                        } else {
                          return { ...slot, selectedWorkers: slot.selectedWorkers.slice(0, newCount) };
                        }
                      }
                      return slot;
                    }));
                  }}
                  size="small"
                  fullWidth
                  disabled={isLocked}
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    height: '36px',
                    fontSize: '0.85rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5b8c6f',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5f847d',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5f847d',
                    },
                    '& .MuiSelect-icon': {
                      color: '#5b8c6f',
                    },
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <MenuItem key={n} value={String(n)}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Row>

            {/* Experience & Appoint Row */}
            <Row sx={{ mt: 2 }}>
              <FormControl>
                <FormLabel>Experience</FormLabel>
                <TextField
                  placeholder="Enter experience needed"
                  size="small"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  type="number"
                  fullWidth
                  disabled={isLocked}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Appoint Member</FormLabel>
                <PaginatedSearchSelect
                  fetchOptions={fetchAppointMemberOptions}
                  getOptionLabel="label"
                  value={appointMember && facilityUsers ?
                    (facilityUsers.find(u => u.user.user_id === Number(appointMember)) ?
                      {
                        id: Number(appointMember),
                        label: `${facilityUsers.find(u => u.user.user_id === Number(appointMember))?.user.first_name} ${facilityUsers.find(u => u.user.user_id === Number(appointMember))?.user.last_name}`
                      }
                      : {
                        id: Number(appointMember),
                        label: (editData && editData.attendance_manager && typeof editData.attendance_manager === 'object' && (String(editData.attendance_manager.id) === String(appointMember) || String(editData.attendance_manager.user_id) === String(appointMember) || !editData.attendance_manager.id))
                          ? `${editData.attendance_manager.first_name} ${editData.attendance_manager.last_name}`
                          : 'Loading...'
                      })
                    : null}
                  onChange={(v: any) => setAppointMember(v ? v.id : '')}
                  placeholder={selectedFacility ? "-Select appoint member-" : "Select Facility to load members"}
                  size='small'
                  fullWidth
                  disabled={isLocked || !selectedFacility}
                />
              </FormControl>
            </Row>

            {/* Label Field - Full Width */}
            <FormControl sx={{ mt: 2 }}>
              <FormLabel>Label</FormLabel>
              <TextField
                placeholder="Enter label (e.g. Area A)"
                size="small"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
              />
            </FormControl>

            {/* Checkboxes Row */}
            <Row sx={{ mt: 1, alignItems: 'center', gap: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSleepover}
                    onChange={(e) => setIsSleepover(e.target.checked)}
                    color="success"
                    disabled={isLocked}
                  />
                }
                label="Sleepover"
              />
              {!editData && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createMultiple}
                      onChange={(e) => setCreateMultiple(e.target.checked)}
                      color="success"
                    />
                  }
                  label="Create Multiple Shifts"
                />
              )}
            </Row>
          </Box>

          {/* Dynamic Shift Slots Section */}
          {shiftSlots.map((slot, index) => (
            <React.Fragment key={index}>
              {(createMultiple || index > 0) && (
                <Row sx={{ justifyContent: 'space-between', alignItems: 'center', mt: index > 0 ? 2 : 1, borderTop: index > 0 ? '1px solid #eee' : 'none', pt: index > 0 ? 1 : 0 }}>
                  {index > 0 ? (
                    <>
                      <Typography variant="subtitle2" sx={{ color: '#555', fontWeight: 600 }}>
                        Shift Slot {index + 1}
                      </Typography>
                      <IconButton onClick={() => handleRemoveSlot(index)} size="small" color="error">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: '#555', fontWeight: 600 }}>
                      Shift Slot 1
                    </Typography>
                  )}
                </Row>
              )}

              <Row>
                <FormControl>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                    <FormLabel sx={{ mb: 0 }}>Start Date</FormLabel>
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <DatePicker
                      value={slot.startDate}
                      onChange={(newValue) => handleSlotChange(index, 'startDate', newValue)}
                      format="DD/MM/YYYY"
                      disabled={isLocked}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </Box>
                </FormControl>
                <FormControl>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ height: '32px' }}>
                    <FormLabel sx={{ mb: 0 }}>End Date</FormLabel>
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={slot.isSameDay}
                          onChange={(e) => handleSlotChange(index, 'isSameDay', e.target.checked)}
                          sx={{ 
                            p: 0.5,
                            color: '#52786F', 
                            '&.Mui-checked': { color: '#52786F' } 
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#555' }}>Same day</Typography>}
                    />
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <DatePicker format="DD/MM/YYYY"
                      value={slot.endDate}
                      disabled={isLocked || slot.isSameDay}
                      onChange={(newValue) => handleSlotChange(index, 'endDate', newValue)}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </Box>
                </FormControl>
              </Row>

              <Row>
                <FormControl>
                  <FormLabel>Shift Starting Time</FormLabel>
                  <Box sx={{ position: 'relative' }}>
                    <TimePicker
                      referenceDate={slot.startDate || dayjs()}
                      value={slot.startTime}
                      onChange={(newValue) => handleSlotChange(index, 'startTime', newValue)}
                      disabled={isLocked}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                      ampm={true}
                    />
                  </Box>
                </FormControl>
                <FormControl>
                  <FormLabel>Shift Ending Time</FormLabel>
                  <Box sx={{ position: 'relative' }}>
                    <TimePicker
                      referenceDate={slot.endDate || dayjs()}
                      value={slot.endTime}
                      onChange={(newValue) => handleSlotChange(index, 'endTime', newValue)}
                      disabled={isLocked}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                      ampm={true}
                    />
                  </Box>
                </FormControl>
              </Row>

              {/* Worker Dropdown for this Slot */}
              {slot.selectedWorkers.map((worker, wIndex) => (
                <FormControl key={wIndex} sx={{ mb: 2 }}>
                  <FormLabel>Worker {slot.selectedWorkers.length > 1 ? wIndex + 1 : ''} {isPastSlot(slot) ? '(Past Shift)' : '(Optional)'}</FormLabel>
                  <PaginatedSearchSelect<Option>
                    label=""
                    placeholder="Search & Select Worker"
                    fetchOptions={(q, p) => fetchWorkerOptions(q, p, slot)}
                    getOptionLabel="label"
                    value={worker}
                    onChange={(newValue) => {
                      const updatedWorkers = [...slot.selectedWorkers];
                      updatedWorkers[wIndex] = newValue;
                      handleSlotChange(index, 'selectedWorkers', updatedWorkers);
                    }}
                  />
                </FormControl>
              ))}
            </React.Fragment>
          ))}

          {createMultiple && (
            <Row sx={{ justifyContent: 'flex-end', mt: 1 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddSlot}
                sx={{
                  color: '#52786F',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
                }}
              >
                Add More Slots
              </Button>
            </Row>
          )}

          {/* Notes */}
          <FormControl sx={{ mt: 1 }}>
            <FormLabel>Additional Notes</FormLabel>
            <TextField
              placeholder="Add any notes..."
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
            />
          </FormControl>
        </LocalizationProvider>
      </ModalContent>

      <ActionButtons sx={{ px: 3, pb: 3, pt: 1, borderTop: '1px solid #eee' }}>
        <CancelButton variant="outlined" onClick={onClose}>
          Cancel
        </CancelButton>
        <SubmitButton variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Processing...' : (editData ? 'Update Shift' : 'Post New Shift')}
        </SubmitButton>
      </ActionButtons>

      {alert && (
        <AlertPopup
          open={!!alert}
          message={alert.message}
          severity={alert.severity}
          onClose={() => setAlert(null)}
        />
      )}
    </StyledDialog>
  );
};

export default CreateShiftModal;
